import { useArbeitgeberFunnel, AG_PHASE_COUNT } from './useArbeitgeberFunnel'
import type { AGFunnelHook } from './useArbeitgeberFunnel'
import ArbeitgeberQuestions from './ArbeitgeberQuestions'
import { MedilaneLockup } from '../../components/brand/Logo'
import '../funnel/funnel.css'

const PHASE_LABELS: Record<number, string> = {
  1: 'Einrichtung',
  2: 'Bedarf',
  3: 'Vergütung & Benefits',
  4: 'Dienstplan & Fakten',
  5: 'Arbeitsbedingungen',
  6: 'Onboarding & Service',
  7: 'Abschluss',
}

export default function ArbeitgeberFunnelLayout() {
  const funnel = useArbeitgeberFunnel()

  if (funnel.submitted) {
    return <SuccessPage funnel={funnel} />
  }

  const { currentQuestion, currentPhase, phaseSubIndex, phaseSubTotal, phaseProgress } = funnel
  const phases = Array.from({ length: AG_PHASE_COUNT }, (_, i) => i + 1)

  return (
    <div className="funnel-page">
      <div className="funnel-container">
        <header className="funnel-header">
          <MedilaneLockup size={32} />
        </header>

        <div className="ag-funnel-intro">Für Arbeitgeber · Einrichtungsprofil</div>

        {funnel.hasRecovery && (
          <div className="recovery-banner animate-fade-in-up">
            <div className="recovery-content">
              <span>👋</span>
              <div>
                <strong>Willkommen zurück!</strong>
                <p>Sie haben bereits Daten eingegeben. Möchten Sie weitermachen?</p>
              </div>
            </div>
            <div className="recovery-actions">
              <button className="funnel-btn funnel-btn--primary funnel-btn--sm" onClick={funnel.recoverState}>
                Weitermachen
              </button>
              <button className="funnel-btn funnel-btn--ghost funnel-btn--sm" onClick={funnel.dismissRecovery}>
                Neu starten
              </button>
            </div>
          </div>
        )}

        <div className="funnel-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${phaseProgress}%` }} />
          </div>
          <div className="progress-steps">
            {phases.map((phase) => (
              <button
                key={phase}
                className={`progress-step ${phase === currentPhase ? 'progress-step--active' : ''} ${phase < currentPhase ? 'progress-step--done' : ''}`}
                onClick={() => phase < currentPhase && funnel.goToPhase(phase)}
                disabled={phase > currentPhase}
                title={PHASE_LABELS[phase]}
              >
                <span className="progress-step-dot">{phase < currentPhase ? '✓' : phase}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="phase-header">
          <div className="phase-header-inner">
            <span className="phase-icon">{currentQuestion.phaseIcon}</span>
            <div className="phase-info">
              <span className="phase-label">{currentQuestion.phaseLabel}</span>
              {phaseSubTotal > 1 && (
                <div className="sub-dots">
                  {Array.from({ length: phaseSubTotal }, (_, i) => (
                    <span
                      key={i}
                      className={`sub-dot ${i === phaseSubIndex ? 'sub-dot--active' : ''} ${i < phaseSubIndex ? 'sub-dot--done' : ''}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="question-wrapper" key={currentQuestion.questionId}>
          <div className="question-card animate-slide-in">
            <h2 className="question-title">{currentQuestion.questionLabel}</h2>
            <div className="question-content">
              <ArbeitgeberQuestions funnel={funnel} questionId={currentQuestion.questionId} />
            </div>
          </div>
        </div>

        {currentQuestion.questionId !== 'consent' && (
          <div className="funnel-nav">
            {!funnel.isFirstQuestion && (
              <button className="funnel-btn funnel-btn--secondary" onClick={funnel.prev}>
                ← Zurück
              </button>
            )}
            <button className="funnel-btn funnel-btn--primary" onClick={funnel.next}>
              Weiter →
            </button>
          </div>
        )}

        <footer className="funnel-footer">
          <span>🔒 Ihre Angaben werden vertraulich behandelt</span>
          <span>Je ehrlicher die Angaben, desto passgenauer das Matching</span>
        </footer>
      </div>
    </div>
  )
}

function SuccessPage({ funnel }: { funnel: AGFunnelHook }) {
  return (
    <div className="funnel-page">
      <div className="funnel-container">
        <div className="funnel-success animate-scale-in">
          <div className="success-icon">🎉</div>
          <h1>Vielen Dank!</h1>
          <p>Ihr Einrichtungsprofil wurde erfolgreich übermittelt.</p>
          <p className="success-detail">
            Medi-Lane gleicht Ihre Einrichtung nun mit wechselbereiten Pflegekräften ab und meldet
            sich mit passenden Profilen bei Ihnen.
          </p>
          <div className="success-summary">
            <span>
              🏢 Eingetragene Einrichtung: <strong>{funnel.data.unternehmensname}</strong>
            </span>
          </div>
          <button className="funnel-btn funnel-btn--secondary" onClick={funnel.reset}>
            Weiteres Einrichtungsprofil anlegen
          </button>
        </div>
      </div>
    </div>
  )
}
